import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { rowPaidAmount } from '../../../../../utils/billingUtils';

const numberToWords = (num) => {
    const a = [
        '', 'one ', 'two ', 'three ', 'four ', 'five ', 'six ', 'seven ', 'eight ', 'nine ', 'ten ', 'eleven ', 'twelve ', 'thirteen ', 'fourteen ', 'fifteen ', 'sixteen ', 'seventeen ', 'eighteen ', 'nineteen '
    ];
    const b = [
        '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'
    ];

    if ((num = num.toString()).length > 9) return 'overflow';
    const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!n) return;
    let str = '';
    str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'crore ' : '';
    str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'lakh ' : '';
    str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'thousand ' : '';
    str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'hundred ' : '';
    str += (n[5] != 0) ? ((str != '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) + 'only ' : '';
    return str;
};

export const generateOrderPDF = (order, billing, dedupedHistory, id) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    const totalAmount = billing?.total || 0;
    const discount = billing?.discountValue || 0;
    const discountRaw = billing?.discountRate || 0;
    const isPercentage = billing?.discountType === 'percentage';
    const finalAmount = billing?.netAmount || 0;
    const paidAmount = billing?.paid || 0;

    // --- Header Section ---
    doc.setFillColor(255, 216, 168);
    doc.rect(0, 0, pageWidth, 80, 'F');

    const logoImg = new Image();
    logoImg.src = '/prakruti-logo.png';
    try {
        doc.addImage(logoImg, 'PNG', pageWidth / 2 - 20, 5, 40, 30);
    } catch (error) {
        console.log('Logo not loaded');
    }

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('PRAKRUTI AYURVEDIC HEALTH CENTRE AND HOSPITAL', pageWidth / 2, 45, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('(A unit of Prakruti Ayurvedic Health Resorts Private Limited)', pageWidth / 2, 55, { align: 'center' });
    doc.text('Address: A/P Yevateshwar, Satara 415002, Maharashtra, India', pageWidth / 2, 60, { align: 'center' });
    doc.text('Reg Office: 177, Budhwar Peth, Parale Sankulan, Satara-415002, Maharashtra, India.', pageWidth / 2, 65, { align: 'center' });
    doc.text('Email: feelbetter@prakruti.health   Phone: 9175242908', pageWidth / 2, 70, { align: 'center' });

    yPosition = 85;

    // --- Patient Information ---
    doc.setFillColor(2, 62, 26);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('PATIENT INFORMATION', 15, yPosition + 7);
    yPosition += 15;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const patientName = order?.additionalInfo?.customerName || order?.additionalInfo?.name || '---';
    const patientDetails = [
        ['Patient Name:', patientName],
        ['Phone:', order?.additionalInfo?.phoneNo || '---'],
        ['Email:', order?.additionalInfo?.email || '---'],
        ['Gender:', order?.customerId?.gender || '---'],
        ['Registration No:', order?.customerId?.registrationNo || '---'],
        ['Order ID:', order?.orderUniqueId || '---'],
        ['Created By:', order?.createdByName || '---'],
        ['Collected By:', order?.updatedByName || '---'],
    ];

    const currentPaidAmount = order?.updatedPaidAmount || billing?.paid || 0;

    patientDetails.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(label, 15, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(value, 60, yPosition);
        yPosition += 7;
    });

    yPosition += 5;

    // --- Order Summary ---
    doc.setFillColor(243, 244, 246);
    doc.rect(10, yPosition, pageWidth - 20, 10, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('ORDER SUMMARY', 15, yPosition + 7);
    yPosition += 15;

    doc.setFontSize(10);
    const pendingAmount = billing?.pending || 0;

    const summaryDetails = [
        ['Total Amount:', `Rs. ${totalAmount}`],
        ['Discount:', `Rs. ${discount}${isPercentage ? ` (${discountRaw}%)` : ''}`],
        ['Net Payable:', `Rs. ${finalAmount}`],
        ['Paid Amount:', `Rs. ${currentPaidAmount}`],
        ['Pending Amount:', `Rs. ${pendingAmount > 0 ? pendingAmount : 0}`],
    ];

    summaryDetails.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(label, 15, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(value, 60, yPosition);
        yPosition += 7;
    });

    yPosition += 10;

    // --- Order History Table ---
    if (dedupedHistory?.length > 0) {
        if (yPosition > pageHeight - 60) {
            doc.addPage();
            yPosition = 20;
        }

        const tableBody = dedupedHistory.map((item, index) => {
            let notes = item.additionalInfo?.reason || '';
            if (item.additionalInfo?.reason === 'Room Upgrade') {
                notes += ` (+ Rs. ${item.additionalInfo?.priceDelta || 0})`;
            }

            return [
                index + 1,
                item.createdAt ? moment(item.createdAt).format('DD/MM/YYYY') : '---',
                item.orderStatus || '---',
                item.paymentMode || '---',
                `Rs. ${item.paidAmount || 0}`,
                `Rs. ${item.amount || 0}`,
                notes,
                item.createdByName || '---',
                item.updatedByName || '---',
            ];
        });

        autoTable(doc, {
            startY: yPosition,
            head: [['#', 'Date', 'Status', 'Mode', 'Paid', 'Total', 'Notes', 'Created By', 'Collected By']],
            body: tableBody,
            theme: 'grid',
            headStyles: { fillColor: [2, 62, 26], textColor: [255, 255, 255] },
            styles: { fontSize: 8 },
            margin: { left: 10, right: 10 },
        });

        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(128, 128, 128);
            doc.text(
                `Page ${i} of ${totalPages} | Generated on ${moment().format('DD/MM/YYYY HH:mm')}`,
                pageWidth / 2,
                pageHeight - 10,
                { align: 'center' }
            );
        }

        // --- Append Payment Receipts ---
        dedupedHistory.forEach((item) => {
            const historyPaid = rowPaidAmount(item);
            if (historyPaid && historyPaid > 0) {
                doc.addPage();
                doc.setFillColor(255, 255, 255);
                doc.rect(0, 0, pageWidth, 50, 'F');

                try {
                    doc.addImage(logoImg, 'PNG', 15, 15, 25, 20);
                } catch (error) {
                    // Logo failed to load, continue without it
                }

                doc.setTextColor(0, 0, 0);
                doc.setFontSize(16);
                doc.text('Prakruti Ayurvedic Health Center and Hospital', 50, 20);

                doc.setFontSize(9);
                doc.text('(A unit of Prakruti Ayurvedic Health Resorts Private Limited)', 50, 26);
                doc.text('Yavateshwar Maharashtra 415002', 50, 30);
                doc.text('India', 50, 34);
                doc.text('7888038690', 50, 38);

                doc.setDrawColor(200, 200, 200);
                doc.line(15, 45, pageWidth - 15, 45);

                doc.setFontSize(14);
                doc.text('PAYMENT RECEIPT', pageWidth / 2, 60, { align: 'center' });

                doc.setDrawColor(180, 180, 180);
                doc.roundedRect(15, 70, pageWidth - 30, 100, 3, 3);

                let receiptY = 85;
                const receiptDetails = [
                    ['Receipt No.', item._id ? item._id.substring(0, 8).toUpperCase() : '---'],
                    ['Payment Date', item.createdAt ? moment(item.createdAt).format('YYYY-MM-DD') : '---'],
                    ['Payment Mode', item.paymentMode || 'Cash'],
                    ['Amount Received In Words', `Indian Rupee ${numberToWords(Math.round(historyPaid))} Only`]
                ];

                receiptDetails.forEach(([label, value]) => {
                    doc.setFontSize(10);
                    doc.text(label, 20, receiptY);
                    doc.text(value, 80, receiptY);
                    receiptY += 15;
                });

                doc.setFillColor(76, 175, 80);
                doc.rect(pageWidth - 70, 70, 55, 40, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(10);
                doc.text('Amount Received', pageWidth - 42.5, 80, { align: 'center' });
                doc.setFontSize(20);
                doc.text(`${Math.round(historyPaid)}`, pageWidth - 42.5, 92, { align: 'center' });

                doc.setTextColor(0, 0, 0);
                doc.setFontSize(12);
                doc.text('Received From', 15, 190);
                doc.text(patientName, 15, 200);
                doc.text('Authorized Signature', pageWidth - 65, 190);
                doc.line(pageWidth - 65, 210, pageWidth - 15, 210);
            }
        });
    }

    doc.save(`OrderHistory_${order?.orderUniqueId || id}.pdf`);
};
